(() => {
  'use strict';

  const DRAWER_DEBOUNCE_MS = 220;
  const ALGOLIA_TIMEOUT_MS = 5000;
  const PRODUCT_HITS_PER_PAGE = 12;
  const CONTENT_HITS_PER_PAGE = 100;
  const MOBILE_FILTER_QUERY = '(max-width: 1023px)';

  let searchClient;
  let drawerTimer;
  let pageSearchTimer;
  let drawerRequestId = 0;
  let filterReturnFocus;
  let mobileFilterSnapshot;
  let restoreFilterIsolation;

  const pageState = {
    activeTab: 'products',
    productPage: 0,
    productHits: [],
    productType: new Set(),
    vanillaGrade: new Set(),
    gradeFacetValues: {},
    minPrice: '',
    maxPrice: '',
    appliedMinPrice: '',
    appliedMaxPrice: '',
    sortIndex: '',
    requestId: 0,
    results: null,
    loadingMore: false,
  };

  const escapeHtml = value => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const escapeRegExp = value => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const highlightText = (value, query) => {
    const text = escapeHtml(value);
    const terms = String(query ?? '').trim();
    if (!terms) return text;
    return text.replace(new RegExp(`(${escapeRegExp(escapeHtml(terms))})`, 'ig'), '<strong>$1</strong>');
  };

  const getStorefrontRoot = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const storefrontPath = path => `${getStorefrontRoot()}${String(path).replace(/^\//, '')}`;
  const localizeStorefrontUrl = url => {
    if (typeof url !== 'string' || !url.startsWith('/')) return url;
    const root = getStorefrontRoot();
    if (root === '/') return url;
    const localizedRoot = root.replace(/\/$/, '');
    if (url === localizedRoot || url.startsWith(`${localizedRoot}/`)) return url;
    return `${localizedRoot}${url}`;
  };
  const getSearchUrl = query => `${storefrontPath('search')}?q=${encodeURIComponent(query)}`;

  const waitForAlgolia = async () => {
    const startedAt = Date.now();

    while (Date.now() - startedAt < ALGOLIA_TIMEOUT_MS) {
      const algolia = window.algoliaShopify;
      if (
        algolia?.config?.app_id
        && algolia?.config?.search_api_key
        && algolia?.externals?.algoliasearch
      ) {
        searchClient ||= algolia.externals.algoliasearch(
          algolia.config.app_id,
          algolia.config.search_api_key,
        );
        return { algolia, client: searchClient };
      }

      await new Promise(resolve => window.setTimeout(resolve, 50));
    }

    throw new Error('Algolia search runtime is unavailable');
  };

  const getIndexName = (algolia, type) => `${algolia.config.index_prefix || ''}${type}`;

  const getHitUrl = (hit, type) => {
    if (
      typeof hit.url === 'string'
      && hit.url.startsWith('/')
      && !hit.url.startsWith('//')
    ) return localizeStorefrontUrl(hit.url);

    const handle = encodeURIComponent(hit.handle || '');
    if (type === 'products') return storefrontPath(`products/${handle}`);
    if (type === 'articles') {
      return storefrontPath(`blogs/${encodeURIComponent(hit.blog?.handle || 'recipes-stories')}/${handle}`);
    }
    return storefrontPath(`pages/${handle}`);
  };

  const getProductImage = hit => {
    const image = hit.product_image || hit.image;
    if (typeof image === 'string') return image;
    return image?.src || '';
  };

  const getCurrencyContext = () => {
    const baseCurrency = document.body.dataset.shopCurrency || 'USD';
    const requestedCurrency = window.Shopify?.currency?.active
      || document.body.dataset.activeCurrency
      || baseCurrency;
    const runtimeRate = Number(window.Shopify?.currency?.rate);
    const canConvert = requestedCurrency === baseCurrency
      || (Number.isFinite(runtimeRate) && runtimeRate > 0);

    return {
      baseCurrency,
      activeCurrency: canConvert ? requestedCurrency : baseCurrency,
      rate: requestedCurrency === baseCurrency || !canConvert ? 1 : runtimeRate,
    };
  };

  const formatCurrencyAmount = (amount, currency, options = {}) => {
    const formatOptions = {
      style: 'currency',
      currency,
    };
    if (Number.isInteger(options.maximumFractionDigits)) {
      formatOptions.maximumFractionDigits = options.maximumFractionDigits;
    }
    return new Intl.NumberFormat(
      document.documentElement.lang || undefined,
      formatOptions,
    ).format(amount);
  };

  const formatPrice = (hit, algolia) => {
    const price = hit.variants_min_price ?? hit.price;
    if (price === undefined || price === null || Number.isNaN(Number(price))) return '';

    const hasPriceRange = Number(hit.variants_count) > 1
      || Number(hit.variants_max_price) > Number(price);
    const currencyContext = getCurrencyContext();
    const amount = Number(price) * currencyContext.rate;
    const currency = currencyContext.activeCurrency;
    let formatted;

    if (currency === currencyContext.baseCurrency && typeof algolia.formatMoney === 'function') {
      formatted = algolia.formatMoney(amount * 100);
    } else if (currency === currencyContext.baseCurrency && typeof window.Shopify?.formatMoney === 'function') {
      formatted = window.Shopify.formatMoney(amount * 100, document.body.dataset.moneyFormat);
    } else {
      formatted = formatCurrencyAmount(amount, currency);
    }

    return `${hasPriceRange ? 'from ' : ''}${formatted}`;
  };

  const renderProductCard = (hit, algolia, result) => {
    const url = getHitUrl(hit, 'products');
    const image = getProductImage(hit);
    const title = escapeHtml(hit.title);
    const productId = escapeHtml(hit.id || hit.objectID);

    return `
      <article
        class="sfg-search-card"
        data-algolia-index="${escapeHtml(result.index)}"
        data-algolia-queryid="${escapeHtml(result.queryID)}"
        data-algolia-objectid="${escapeHtml(hit.objectID)}"
      >
        <a class="sfg-search-card__image" href="${escapeHtml(url)}">
          ${image ? `<img src="${escapeHtml(image)}" alt="${title}" loading="lazy">` : ''}
        </a>
        <a class="sfg-search-card__title" href="${escapeHtml(url)}">${title}</a>
        <span class="sfg-search-card__price">${escapeHtml(formatPrice(hit, algolia))}</span>
        <span
          class="sfg-search-card__reviews stamped-product-reviews-badge stamped-main-badge"
          data-id="${productId}"
          data-product-sku="${escapeHtml(hit.sku)}"
          data-product-title="${title}"
          data-product-type="${escapeHtml(hit.product_type)}"
        ></span>
      </article>
    `;
  };

  const renderContentGroup = (heading, hits, type, { query = '', limit } = {}) => {
    const visibleHits = Number.isInteger(limit) ? hits.slice(0, limit) : hits;
    if (!visibleHits.length) return '';

    return `
      <section class="sfg-search-resources__group">
        <h2>${heading}</h2>
        ${visibleHits.map(hit => `<a href="${escapeHtml(getHitUrl(hit, type))}">${highlightText(hit.title, query)}</a>`).join('')}
      </section>
    `;
  };

  const getDrawerSupportLinks = form => {
    const group = [...form.querySelectorAll('#sfg-search-default-content .sfg-search-resources__group')]
      .find(item => item.querySelector('h2')?.textContent.trim().toLowerCase() === 'support');

    return [...group?.querySelectorAll('a') || []].slice(0, 3).map(link => ({
      title: link.textContent.trim(),
      url: link.getAttribute('href'),
    }));
  };

  const getCarouselProgress = carousel => {
    const shell = carousel.closest('.sfg-search-carousel-shell');
    const progress = shell ? shell.nextElementSibling : carousel.nextElementSibling;
    return progress?.classList.contains('sfg-search-carousel-progress') ? progress : null;
  };

  const updateCarouselProgress = carousel => {
    const progress = getCarouselProgress(carousel);
    if (!progress?.classList.contains('sfg-search-carousel-progress')) return;

    const trackWidth = 163.75;
    const maxScroll = Math.max(0, carousel.scrollWidth - carousel.clientWidth);
    const thumbWidth = 44;
    const offset = maxScroll ? (carousel.scrollLeft / maxScroll) * (trackWidth - thumbWidth) : 0;
    const nextButton = carousel.closest('.sfg-search-carousel-shell')
      ?.querySelector('[data-sfg-carousel-next]');
    progress.style.setProperty('--sfg-carousel-thumb', `${thumbWidth}px`);
    progress.style.setProperty('--sfg-carousel-offset', `${offset}px`);
    progress.hidden = false;
    if (nextButton) {
      const atEnd = maxScroll > 1 && carousel.scrollLeft >= maxScroll - 1;
      nextButton.dataset.sfgCarouselNextLabel ||= nextButton.getAttribute('aria-label') || 'Scroll products right';
      nextButton.dataset.sfgCarouselWrap = atEnd ? 'true' : 'false';
      nextButton.setAttribute(
        'aria-label',
        atEnd ? 'Return to the first product' : nextButton.dataset.sfgCarouselNextLabel,
      );
      nextButton.hidden = maxScroll <= 1;
    }
  };

  const initializeCarousel = carousel => {
    if (!carousel || carousel.dataset.sfgCarouselReady === 'true') return;
    const progress = getCarouselProgress(carousel);
    if (!progress?.classList.contains('sfg-search-carousel-progress')) return;

    carousel.dataset.sfgCarouselReady = 'true';
    carousel.addEventListener('scroll', () => updateCarouselProgress(carousel), { passive: true });

    // When a customer swipes forward again from the final card, mirror the
    // desktop wrap action and return them to the first product.
    let touchStartX = null;
    let touchStartedAtEnd = false;
    carousel.addEventListener('touchstart', event => {
      const touch = event.changedTouches[0];
      const maxScroll = Math.max(0, carousel.scrollWidth - carousel.clientWidth);
      touchStartX = touch ? touch.clientX : null;
      touchStartedAtEnd = maxScroll > 1 && carousel.scrollLeft >= maxScroll - 1;
    }, { passive: true });
    carousel.addEventListener('touchend', event => {
      const touch = event.changedTouches[0];
      const swipedForward = touchStartX !== null && touch && touchStartX - touch.clientX > 24;
      if (touchStartedAtEnd && swipedForward) {
        carousel.scrollTo({
          left: 0,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      }
      touchStartX = null;
      touchStartedAtEnd = false;
    }, { passive: true });
    window.requestAnimationFrame(() => updateCarouselProgress(carousel));
  };

  const initializeCarousels = root => {
    root.querySelectorAll?.('[data-sfg-search-carousel]').forEach(initializeCarousel);
  };

  const refreshCarousels = root => {
    window.requestAnimationFrame(() => {
      root?.querySelectorAll?.('[data-sfg-search-carousel]')
        .forEach(updateCarouselProgress);
    });
  };

  const reloadProductWidgets = container => {
    window.StampedFn?.reloadUGC?.();
    window.rimg?.watch?.(container);
    initializeCarousels(container);
  };

  const renderSuggestions = (hits, query) => {
    const seen = new Set();
    const suggestions = hits
      .map(hit => String(hit.title || '')
        .replace(/^(gourmet|organic|extract grade [a-z])\s+/i, '')
        .split(',')[0]
        .trim())
      .filter(suggestion => {
        const key = suggestion.toLowerCase();
        if (!suggestion || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .slice(0, 4);

    if (!suggestions.length) return '';

    return `
      <nav class="predictive-search__queries-collections" aria-label="Related searches">
        <ul class="predictive-search__queries-collections-list">
          ${suggestions.map(suggestion => `
            <li>
              <a class="predictive-search__query-link" href="${getSearchUrl(suggestion)}">
                ${highlightText(suggestion, query)}
              </a>
            </li>
          `).join('')}
        </ul>
      </nav>
    `;
  };

  const renderDrawerResults = ({ algolia, products, articles, pages, query, form }) => {
    const results = form.querySelector('[data-results-wrapper]');
    if (!results) return;

    const productHits = products.hits || [];
    const articleHits = articles.hits || [];
    const pageHits = pages.hits || [];
    const productCount = products.nbHits || 0;
    const contentCount = (articles.nbHits || 0) + (pages.nbHits || 0);
    const supportHits = [...pageHits.slice(0, 1), ...getDrawerSupportLinks(form)]
      .filter((hit, index, hits) => (
        hits.findIndex(candidate => (candidate.url || candidate.handle) === (hit.url || hit.handle)) === index
      ))
      .slice(0, 3);

    if (productCount + contentCount === 0) {
      const template = form.querySelector('[data-sfg-search-empty-template]');
      results.replaceChildren(template?.content.cloneNode(true) || document.createTextNode(''));
      const title = results.querySelector('[data-sfg-empty-title]');
      if (title) title.textContent = `No results found for "${query}"`;
      results.classList.add('predictive-search__results-wrapper--empty');
      reloadProductWidgets(results);
      return;
    }

    const activePanel = productCount ? 'products' : 'content';
    results.classList.remove('predictive-search__results-wrapper--empty');
    results.innerHTML = `
      <div class="predictive-search__results">
        ${renderSuggestions(productHits, query)}
        <div class="sfg-search-tabs" role="tablist" aria-label="Search result types">
          ${productCount ? `
            <button id="sfg-drawer-products-tab" class="sfg-search-tabs__button ${activePanel === 'products' ? 'is-active' : ''}" type="button" role="tab" aria-selected="${activePanel === 'products'}" aria-controls="sfg-drawer-products-panel" tabindex="${activePanel === 'products' ? '0' : '-1'}" data-sfg-search-tab="products">
              Products (${productCount})
            </button>
          ` : ''}
          ${contentCount ? `
            <button id="sfg-drawer-content-tab" class="sfg-search-tabs__button ${activePanel === 'content' ? 'is-active' : ''}" type="button" role="tab" aria-selected="${activePanel === 'content'}" aria-controls="sfg-drawer-content-panel" tabindex="${activePanel === 'content' ? '0' : '-1'}" data-sfg-search-tab="content">
              Education &amp; Recipes (${contentCount})
            </button>
          ` : ''}
        </div>
        ${productCount ? `
          <section id="sfg-drawer-products-panel" class="predictive-search__products" role="tabpanel" aria-labelledby="sfg-drawer-products-tab" data-sfg-search-panel="products" ${activePanel !== 'products' ? 'hidden' : ''}>
            <div class="predictive-search__products-list">
              ${productHits.map(hit => renderProductCard(hit, algolia, products)).join('')}
            </div>
          </section>
        ` : ''}
        ${contentCount ? `
          <div id="sfg-drawer-content-panel" class="predictive-search__pages-posts" role="tabpanel" aria-labelledby="sfg-drawer-content-tab" data-sfg-search-panel="content" ${activePanel !== 'content' ? 'hidden' : ''}>
            ${renderContentGroup('Education &amp; Recipes', articleHits, 'articles', { query, limit: 2 })}
            ${renderContentGroup('Support', supportHits.length ? supportHits : pageHits, 'pages', { limit: 3 })}
          </div>
        ` : ''}
        <a class="predictive-search__search-for-btn" href="${getSearchUrl(query)}">View All Results</a>
      </div>
    `;

    reloadProductWidgets(results);
  };

  const renderDrawerError = (form, query) => {
    const results = form.querySelector('[data-results-wrapper]');
    if (!results) return;
    results.classList.remove('predictive-search__results-wrapper--empty');
    results.innerHTML = `
      <div class="predictive-search__error" role="status">
        <strong>Search is temporarily unavailable.</strong>
        <a class="predictive-search__search-for-btn" href="${getSearchUrl(query)}">View search results</a>
      </div>
    `;
  };

  const requestDrawerResults = async (query, form) => {
    const { algolia, client } = await waitForAlgolia();
    const response = await client.search([
      {
        indexName: getIndexName(algolia, 'products'),
        query,
        params: { clickAnalytics: true, distinct: true, hitsPerPage: 6 },
      },
      {
        indexName: getIndexName(algolia, 'articles'),
        query,
        params: { clickAnalytics: true, hitsPerPage: 6 },
      },
      {
        indexName: getIndexName(algolia, 'pages'),
        query,
        params: { clickAnalytics: true, hitsPerPage: 6 },
      },
    ]);

    return {
      algolia,
      products: response.results[0],
      articles: response.results[1],
      pages: response.results[2],
      query,
      form,
    };
  };

  const setDrawerDefaultVisibility = input => {
    const form = input.closest('[data-search-form]');
    const defaultContent = form?.querySelector('[data-sfg-search-default]');
    if (defaultContent) defaultContent.hidden = input.value.trim().length > 0;
  };

  const handleDrawerInput = (input, immediate = false) => {
    if (!input?.matches('[data-predictive-search-terms]')) return;

    setDrawerDefaultVisibility(input);
    const form = input.closest('[data-search-form]');
    const results = form?.querySelector('[data-results-wrapper]');
    const query = input.value.trim();

    window.clearTimeout(drawerTimer);
    if (!results) return;

    if (!query) {
      drawerRequestId += 1;
      results.replaceChildren();
      results.classList.remove('predictive-search__results-wrapper--empty');
      input.setAttribute('aria-expanded', 'false');
      results.setAttribute('aria-busy', 'false');
      return;
    }

    const requestId = ++drawerRequestId;
    results.setAttribute('aria-busy', 'true');
    drawerTimer = window.setTimeout(async () => {
      try {
        const response = await requestDrawerResults(query, form);
        if (requestId !== drawerRequestId) return;
        renderDrawerResults(response);
        input.setAttribute('aria-expanded', 'true');
      } catch (error) {
        if (requestId === drawerRequestId) {
          renderDrawerError(form, query);
          input.setAttribute('aria-expanded', 'true');
        }
      } finally {
        if (requestId === drawerRequestId) results.setAttribute('aria-busy', 'false');
      }
    }, immediate ? 0 : DRAWER_DEBOUNCE_MS);
  };

  const getFocusable = container => [...container.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])',
  )].filter(element => !element.hidden && element.getClientRects().length > 0);

  const isolateModalBranch = branch => {
    const changed = [];
    let activeBranch = branch;

    while (activeBranch && activeBranch !== document.body) {
      const parent = activeBranch.parentElement;
      if (!parent) break;
      [...parent.children].forEach(sibling => {
        if (sibling === activeBranch || sibling.inert) return;
        changed.push(sibling);
        sibling.inert = true;
      });
      activeBranch = parent;
    }

    return () => changed.reverse().forEach(element => {
      if (element.isConnected) element.inert = false;
    });
  };

  const initializeSearchDrawer = () => {
    const overlay = document.querySelector('[data-search-overlay]');
    const drawer = overlay?.querySelector('[data-search]');
    const form = drawer?.querySelector('[data-search-form]');
    const input = form?.querySelector('[data-predictive-search-terms]');
    const closeButton = drawer?.querySelector('[data-close-search]');
    const triggers = [...document.querySelectorAll('[data-search-open]')];
    if (!overlay || !drawer || !form || !input || !closeButton || !triggers.length) return;

    let open = false;
    let openPending = false;
    let returnFocus;
    let restoreSearchIsolation;

    const closeMobileMenu = () => new Promise(resolve => {
      const mobileNav = document.querySelector('[data-mobile-nav][data-open="true"]');
      const mobileNavClose = mobileNav?.querySelector('[data-mobile-nav-close]');
      if (!mobileNav || !mobileNavClose) {
        resolve();
        return;
      }

      let timeout;
      const observer = new MutationObserver(() => {
        if (mobileNav.dataset.open !== 'true') finish();
      });
      const finish = () => {
        observer.disconnect();
        window.clearTimeout(timeout);
        resolve();
      };

      observer.observe(mobileNav, { attributes: true, attributeFilter: ['data-open'] });
      mobileNavClose.click();
      if (mobileNav.dataset.open !== 'true') {
        finish();
        return;
      }
      timeout = window.setTimeout(finish, 350);
    });

    const close = ({ restoreFocus = true } = {}) => {
      if (!open) return;
      open = false;
      overlay.dataset.sfgSearchOpen = 'false';
      overlay.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('sfg-search-drawer-open');
      document.body.classList.remove('sfg-search-drawer-open');
      triggers.forEach(trigger => trigger.setAttribute('aria-expanded', 'false'));
      restoreSearchIsolation?.();
      restoreSearchIsolation = undefined;
      if (restoreFocus && returnFocus?.isConnected) returnFocus.focus();
    };

    const show = trigger => {
      if (open) return;
      open = true;
      returnFocus = trigger;
      overlay.dataset.sfgSearchOpen = 'true';
      overlay.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('sfg-search-drawer-open');
      document.body.classList.add('sfg-search-drawer-open');
      restoreSearchIsolation = isolateModalBranch(overlay);
      triggers.forEach(button => button.setAttribute('aria-expanded', button === trigger ? 'true' : 'false'));
      window.requestAnimationFrame(() => {
        input.focus({ preventScroll: true });
        const end = input.value.length;
        input.setSelectionRange(end, end);
        if (input.value.trim() && !form.querySelector('[data-results-wrapper]')?.children.length) {
          handleDrawerInput(input, true);
        }
        refreshCarousels(form);
      });
    };

    triggers.forEach(trigger => {
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-controls', overlay.id);
      trigger.setAttribute('aria-expanded', 'false');
      trigger.addEventListener('click', async event => {
        event.preventDefault();
        if (open || openPending) return;
        openPending = true;
        await closeMobileMenu();
        openPending = false;
        show(trigger);
      });
    });

    closeButton.addEventListener('click', () => close());
    overlay.addEventListener('click', event => {
      if (event.target === overlay) close();
    });
    form.addEventListener('submit', event => {
      if (!input.value.trim()) event.preventDefault();
    });
    document.addEventListener('keydown', event => {
      if (!open) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = getFocusable(drawer);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    overlay.dataset.sfgSearchOpen = 'false';
  };

  const getSortOptions = algolia => {
    const baseIndex = getIndexName(algolia, 'products');
    const options = [{ value: baseIndex, label: 'Sort' }];

    (algolia.config.sort_orders || []).forEach(order => {
      ['asc', 'desc'].forEach(direction => {
        const setting = order[direction];
        if (setting?.active === true || setting?.active === '1') {
          options.push({
            value: `${baseIndex}_${order.key}_${direction}`,
            label: setting.title || order.title,
          });
        }
      });
    });

    return options;
  };

  const getSupportedProductFacets = algolia => {
    const declaredFacets = new Set(
      (algolia.config.facets || []).map(facet => facet?.name).filter(Boolean),
    );
    const desiredFacets = ['price', 'product_type', 'tags'];
    return desiredFacets.filter(name => !declaredFacets.size || declaredFacets.has(name));
  };

  const buildFacetFilters = (snapshot, { includeProductType = true, includeGrade = true } = {}) => {
    const filters = [];
    if (includeProductType && snapshot.productType.length) {
      filters.push(snapshot.productType.map(value => `product_type:${value}`));
    }
    if (includeGrade && snapshot.vanillaGrade.length) {
      const rawValues = snapshot.vanillaGrade.flatMap(grade => (
        snapshot.gradeFacetValues[grade]?.values || [`Grade ${grade}`]
      ));
      filters.push(rawValues.map(value => `tags:${value}`));
    }
    return filters;
  };

  const buildNumericFilters = snapshot => {
    const filters = [];
    const rate = Number(snapshot.currencyRate) || 1;
    const minPrice = Number(snapshot.minPrice);
    const maxPrice = Number(snapshot.maxPrice);
    if (snapshot.minPrice !== '' && Number.isFinite(minPrice) && minPrice >= 0) {
      filters.push(`price >= ${minPrice / rate}`);
    }
    if (snapshot.maxPrice !== '' && Number.isFinite(maxPrice) && maxPrice >= 0) {
      filters.push(`price <= ${maxPrice / rate}`);
    }
    return filters;
  };

  const createPageSnapshot = productPage => ({
    productPage,
    productType: [...pageState.productType],
    vanillaGrade: [...pageState.vanillaGrade],
    gradeFacetValues: Object.fromEntries(
      Object.entries(pageState.gradeFacetValues).map(([grade, data]) => [
        grade,
        { ...data, values: [...(data.values || [])] },
      ]),
    ),
    minPrice: pageState.minPrice,
    maxPrice: pageState.maxPrice,
    sortIndex: pageState.sortIndex,
    currencyRate: getCurrencyContext().rate,
  });

  const requestPageResults = async (query, snapshot, { loadMore = false } = {}) => {
    const { algolia, client } = await waitForAlgolia();
    const baseIndex = getIndexName(algolia, 'products');
    const sortIndex = snapshot.sortIndex || baseIndex;
    const cachedResponse = pageState.results?.query === query ? pageState.results : null;
    const supportedFacets = getSupportedProductFacets(algolia);
    const supportsFacet = name => supportedFacets.includes(name);
    const commonProductParams = {
      distinct: true,
      facetingAfterDistinct: true,
      numericFilters: supportsFacet('price') ? buildNumericFilters(snapshot) : [],
    };

    const requests = [{
      key: 'products',
      request: {
        indexName: sortIndex,
        query,
        params: {
          ...commonProductParams,
          clickAnalytics: true,
          facets: loadMore ? [] : supportedFacets,
          facetFilters: buildFacetFilters(snapshot, {
            includeProductType: supportsFacet('product_type'),
            includeGrade: supportsFacet('tags'),
          }),
          hitsPerPage: PRODUCT_HITS_PER_PAGE,
          page: snapshot.productPage,
        },
      },
    }];

    if (!cachedResponse) {
      requests.push({
        key: 'articles',
        request: {
          indexName: getIndexName(algolia, 'articles'),
          query,
          params: { clickAnalytics: true, hitsPerPage: CONTENT_HITS_PER_PAGE },
        },
      }, {
        key: 'pages',
        request: {
          indexName: getIndexName(algolia, 'pages'),
          query,
          params: { clickAnalytics: true, hitsPerPage: CONTENT_HITS_PER_PAGE },
        },
      });
    }

    if (!loadMore && supportsFacet('product_type') && snapshot.productType.length) {
      requests.push({
        key: 'productTypes',
        request: {
          indexName: baseIndex,
          query,
          params: {
            ...commonProductParams,
            facets: ['product_type'],
            facetFilters: buildFacetFilters(snapshot, { includeProductType: false }),
            hitsPerPage: 0,
          },
        },
      });
    }

    if (!loadMore && supportsFacet('tags') && snapshot.vanillaGrade.length) {
      requests.push({
        key: 'grades',
        request: {
          indexName: baseIndex,
          query,
          params: {
            ...commonProductParams,
            facets: ['tags'],
            facetFilters: buildFacetFilters(snapshot, { includeGrade: false }),
            hitsPerPage: 0,
          },
        },
      });
    }

    const response = await client.search(requests.map(({ request }) => request));
    const results = Object.fromEntries(
      requests.map(({ key }, index) => [key, response.results[index]]),
    );
    const products = results.products;
    products.sfgSupportedFacets = supportedFacets;

    if (loadMore && cachedResponse) {
      products.facets = cachedResponse.products.facets || {};
      products.facets_stats = cachedResponse.products.facets_stats || {};
    } else {
      products.facets = {
        ...(products.facets || {}),
        product_type: results.productTypes?.facets?.product_type
          || products.facets?.product_type
          || {},
        tags: results.grades?.facets?.tags || products.facets?.tags || {},
      };
    }

    return {
      algolia,
      products,
      articles: results.articles || cachedResponse?.articles || { hits: [], nbHits: 0 },
      pages: results.pages || cachedResponse?.pages || { hits: [], nbHits: 0 },
      query,
      sortIndex,
    };
  };

  const renderCheckboxes = (values, selected, facet) => {
    const visibleValues = Object.entries(values || {})
      .sort((a, b) => b[1] - a[1])
      .slice(0, 12);

    selected.forEach(value => {
      if (!visibleValues.some(([visibleValue]) => visibleValue === value)) {
        visibleValues.push([value, values?.[value] || 0]);
      }
    });

    return visibleValues.map(([value, count]) => `
      <label class="sfg-algolia-facet__option">
        <input type="checkbox" value="${escapeHtml(value)}" data-sfg-facet="${facet}" ${selected.has(value) ? 'checked' : ''}>
        <span>${escapeHtml(value)}</span>
        <small>${count}</small>
      </label>
    `).join('');
  };

  const normalizeGrade = value => value.match(/\bgrade[-_\s]*([abc])\b/i)?.[1]?.toUpperCase();

  const getGradeFacets = tags => {
    const grades = {};
    Object.entries(tags || {}).forEach(([tag, count]) => {
      const grade = normalizeGrade(tag);
      if (!grade) return;
      grades[grade] ||= { count: 0, values: [] };
      grades[grade].count += count;
      grades[grade].values.push(tag);
    });
    return grades;
  };

  const renderGradeCheckboxes = tags => {
    const discoveredGrades = getGradeFacets(tags);
    Object.entries(discoveredGrades).forEach(([grade, data]) => {
      const previousValues = pageState.gradeFacetValues[grade]?.values || [];
      pageState.gradeFacetValues[grade] = {
        count: data.count,
        values: [...new Set([...previousValues, ...data.values])],
      };
    });

    const visibleGrades = { ...discoveredGrades };
    pageState.vanillaGrade.forEach(grade => {
      if (!visibleGrades[grade]) {
        visibleGrades[grade] = {
          count: 0,
          values: [...(pageState.gradeFacetValues[grade]?.values || [`Grade ${grade}`])],
        };
      }
    });

    return Object.entries(visibleGrades)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([grade, data]) => `
        <label class="sfg-algolia-facet__option">
          <input type="checkbox" value="${grade}" data-sfg-facet="vanilla-grade" ${pageState.vanillaGrade.has(grade) ? 'checked' : ''}>
          <span>Grade ${grade}${grade === 'A' ? ' (Gourmet)' : ''}</span>
          <small>${data.count}</small>
        </label>
      `).join('');
  };

  const hasActiveFilters = () => (
    pageState.productType.size > 0
    || pageState.vanillaGrade.size > 0
    || pageState.minPrice !== ''
    || pageState.maxPrice !== ''
  );

  const formatSearchCount = (template, count, query) => template
    .replace('__COUNT__', String(count))
    .replace('__TERMS__', query);

  const syncSearchPageTitle = (mount, count, query) => {
    document.title = formatSearchCount(mount.dataset.resultsTitle, count, query);
  };

  const hasPendingPriceFilter = () => (
    pageState.minPrice !== pageState.appliedMinPrice
    || pageState.maxPrice !== pageState.appliedMaxPrice
  );

  const captureFilterState = () => ({
    productType: new Set(pageState.productType),
    vanillaGrade: new Set(pageState.vanillaGrade),
    minPrice: pageState.minPrice,
    maxPrice: pageState.maxPrice,
  });

  const restoreFilterState = (mount, snapshot) => {
    if (!snapshot) return;
    pageState.productType = new Set(snapshot.productType);
    pageState.vanillaGrade = new Set(snapshot.vanillaGrade);
    pageState.minPrice = snapshot.minPrice;
    pageState.maxPrice = snapshot.maxPrice;

    mount?.querySelectorAll('[data-sfg-facet]').forEach(input => {
      const selected = input.dataset.sfgFacet === 'product-type'
        ? pageState.productType
        : pageState.vanillaGrade;
      input.checked = selected.has(input.value);
    });
    mount?.querySelectorAll('[data-sfg-price]').forEach(input => {
      input.value = input.dataset.sfgPrice === 'min' ? pageState.minPrice : pageState.maxPrice;
    });
  };

  const renderFilters = products => {
    const facets = products.facets || {};
    const stats = products.facets_stats?.price || {};
    const supportedFacets = products.sfgSupportedFacets || ['price', 'product_type', 'tags'];
    const supportsFacet = name => supportedFacets.includes(name);
    const currencyContext = getCurrencyContext();
    const maxPrice = Number.isFinite(stats.max) ? Math.ceil(stats.max * currencyContext.rate) : '';
    const currencySymbol = new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency: currencyContext.activeCurrency,
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(0).find(part => part.type === 'currency')?.value || currencyContext.activeCurrency;
    const productTypes = supportsFacet('product_type')
      ? renderCheckboxes(facets.product_type, pageState.productType, 'product-type')
      : '';
    const grades = supportsFacet('tags') ? renderGradeCheckboxes(facets.tags) : '';

    return `
      <div class="sfg-algolia-filter-drawer__header">
        <strong id="sfg-algolia-filters-title">Filters</strong>
        <button type="button" aria-label="Close filters" data-sfg-close-filters>&times;</button>
      </div>
      ${supportsFacet('price') ? `
        <details class="sfg-algolia-facet" open>
          <summary>Price</summary>
          <div class="sfg-algolia-facet__price">
            <label data-currency-symbol="${escapeHtml(currencySymbol)}"><span>Minimum price</span><input aria-label="Minimum price" type="number" min="0" max="${maxPrice}" value="${escapeHtml(pageState.minPrice)}" placeholder="0" data-sfg-price="min"></label>
            <span aria-hidden="true">to</span>
            <label data-currency-symbol="${escapeHtml(currencySymbol)}"><span>Maximum price</span><input aria-label="Maximum price" type="number" min="0" max="${maxPrice}" value="${escapeHtml(pageState.maxPrice)}" placeholder="${maxPrice || 0}" data-sfg-price="max"></label>
          </div>
          ${maxPrice ? `<p class="sfg-algolia-facet__price-note">The highest price is ${escapeHtml(formatCurrencyAmount(maxPrice, currencyContext.activeCurrency, { maximumFractionDigits: 0 }))}</p>` : ''}
        </details>
      ` : ''}
      ${productTypes ? `
        <details class="sfg-algolia-facet" open>
          <summary>Product Type</summary>
          <div class="sfg-algolia-facet__options">${productTypes}</div>
        </details>
      ` : ''}
      ${grades ? `
        <details class="sfg-algolia-facet" open>
          <summary>Vanilla Grade</summary>
          <div class="sfg-algolia-facet__options">${grades}</div>
        </details>
      ` : ''}
      ${hasActiveFilters() ? '<button class="sfg-algolia-filter-drawer__clear" type="button" data-sfg-clear-filters>Clear filters</button>' : ''}
      <button class="sfg-algolia-filter-drawer__apply" type="button" data-sfg-apply-filters>View Results</button>
    `;
  };

  const getFocusKey = element => {
    if (!element) return '';
    if (element.matches('[data-sfg-page-tab]')) return `tab:${element.dataset.sfgPageTab}`;
    if (element.matches('[data-sfg-facet]')) return `facet:${element.dataset.sfgFacet}:${element.value}`;
    if (element.matches('[data-sfg-price]')) return `price:${element.dataset.sfgPrice}`;
    if (element.matches('[data-sfg-sort]')) return 'sort';
    if (element.matches('[data-sfg-load-more]')) return 'load-more';
    if (element.matches('[data-sfg-open-filters]')) return 'filter-trigger';
    return '';
  };

  const restoreFocus = (mount, focusKey) => {
    if (!focusKey) return;
    let selector;
    if (focusKey.startsWith('tab:')) selector = `[data-sfg-page-tab="${focusKey.slice(4)}"]`;
    if (focusKey.startsWith('price:')) selector = `[data-sfg-price="${focusKey.slice(6)}"]`;
    if (focusKey === 'sort') selector = '[data-sfg-sort]';
    if (focusKey === 'load-more') selector = '[data-sfg-load-more]';
    if (focusKey === 'filter-trigger') selector = '[data-sfg-open-filters]';
    if (focusKey.startsWith('product:')) {
      const objectId = window.CSS.escape(focusKey.slice(8));
      selector = `[data-algolia-objectid="${objectId}"] .sfg-search-card__image`;
    }
    if (focusKey.startsWith('facet:')) {
      const [, facet, ...valueParts] = focusKey.split(':');
      const value = valueParts.join(':');
      selector = `[data-sfg-facet="${window.CSS.escape(facet)}"][value="${window.CSS.escape(value)}"]`;
    }
    window.requestAnimationFrame(() => {
      const target = selector ? mount.querySelector(selector) : null;
      if (target) {
        target.focus();
      } else if (focusKey === 'load-more') {
        mount.querySelector('[data-sfg-results-status]')?.focus();
      } else if (focusKey.startsWith('facet:')) {
        const [, facet] = focusKey.split(':');
        const escapedFacet = window.CSS.escape(facet);
        const fallback = mount.querySelector(`[data-sfg-facet="${escapedFacet}"]`)
          || mount.querySelector('[data-sfg-filters] summary')
          || mount.querySelector('[data-sfg-page-tab="products"]');
        fallback?.focus();
      }
    });
  };

  const renderPageResults = (mount, response, focusKey = '') => {
    const { algolia, products, articles, pages, query } = response;
    const productCount = products.nbHits || 0;
    const contentCount = (articles.nbHits || 0) + (pages.nbHits || 0);
    const totalCount = productCount + contentCount;
    const activeFilters = hasActiveFilters();
    const showProductsTab = productCount > 0 || activeFilters;

    syncSearchPageTitle(mount, totalCount, query);

    if (!totalCount && !activeFilters) {
      const recoveryTemplate = document.querySelector('[data-sfg-algolia-recovery-template]');
      mount.innerHTML = `
        <section class="sfg-algolia-search__empty">
          <h1>No Results Found For "${escapeHtml(query)}"</h1>
          <p>Try searching for something else.</p>
          <form class="sfg-algolia-search__empty-form" action="${storefrontPath('search')}">
            <button type="submit" aria-label="Search">
              <svg width="18" height="20" viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M14.4038 7.7019C14.4038 11.4033 11.4033 14.4038 7.7019 14.4038C4.00054 14.4038 1 11.4033 1 7.7019C1 4.00054 4.00054 1 7.7019 1C11.4033 1 14.4038 4.00054 14.4038 7.7019ZM12.1015 14.0243C10.8543 14.8939 9.33765 15.4038 7.7019 15.4038C3.44826 15.4038 0 11.9555 0 7.7019C0 3.44826 3.44826 0 7.7019 0C11.9555 0 15.4038 3.44826 15.4038 7.7019C15.4038 9.95817 14.4336 11.9878 12.8876 13.3965L17.7842 18.293C17.9794 18.4883 17.9794 18.8048 17.7842 19.0001C17.5889 19.1954 17.2723 19.1954 17.0771 19.0001L12.1114 14.0344C12.108 14.0311 12.1048 14.0277 12.1015 14.0243Z" fill="currentColor"></path>
              </svg>
            </button>
            <label class="visually-hidden" for="sfg-empty-search-input">Search</label>
            <input id="sfg-empty-search-input" type="search" name="q" placeholder="Search" autocomplete="off">
          </form>
        </section>
        <div data-sfg-recovery-slot></div>
      `;
      const recoverySlot = mount.querySelector('[data-sfg-recovery-slot]');
      if (recoveryTemplate && recoverySlot) {
        recoverySlot.replaceChildren(recoveryTemplate.content.cloneNode(true));
      }
      document.documentElement.classList.add('sfg-algolia-search-ready');
      document.documentElement.classList.remove('sfg-algolia-search-booting');
      mount.classList.remove('is-content-active');
      reloadProductWidgets(mount);
      restoreFocus(mount, focusKey);
      return;
    }

    if (!showProductsTab && pageState.activeTab === 'products') pageState.activeTab = 'content';
    if (!contentCount && pageState.activeTab === 'content') pageState.activeTab = 'products';
    const sortOptions = getSortOptions(algolia);
    const activeProducts = pageState.activeTab === 'products';

    mount.innerHTML = `
      <section class="sfg-algolia-search__header">
        <h1>${escapeHtml(formatSearchCount(mount.dataset.resultsHeading, totalCount, query))}</h1>
        <div class="sfg-search-tabs sfg-algolia-search__tabs" role="tablist" aria-label="Search result types">
          ${showProductsTab ? `<button id="sfg-page-products-tab" class="sfg-search-tabs__button ${activeProducts ? 'is-active' : ''}" type="button" role="tab" aria-selected="${activeProducts}" aria-controls="sfg-page-products-panel" tabindex="${activeProducts ? '0' : '-1'}" data-sfg-page-tab="products">Products (${productCount})</button>` : ''}
          ${contentCount ? `<button id="sfg-page-content-tab" class="sfg-search-tabs__button ${!activeProducts ? 'is-active' : ''}" type="button" role="tab" aria-selected="${!activeProducts}" aria-controls="sfg-page-content-panel" tabindex="${!activeProducts ? '0' : '-1'}" data-sfg-page-tab="content">Education &amp; Recipes (${contentCount})</button>` : ''}
        </div>
      </section>
      <div class="sfg-algolia-search__toolbar" ${!activeProducts ? 'hidden' : ''}>
        <h2 class="sfg-algolia-search__filter-heading">Filters</h2>
        <button class="sfg-algolia-search__filter-button" type="button" aria-controls="sfg-algolia-filters" aria-expanded="false" data-sfg-open-filters>
          <span>Filters</span>
          <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 6h16M7 12h10M10 18h4" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>
        </button>
        <span>${productCount} Products</span>
        <label class="sfg-algolia-search__sort">
          <span class="visually-hidden">Sort search results</span>
          <select aria-label="Sort search results" data-sfg-sort>
            ${sortOptions.map(option => `<option value="${escapeHtml(option.value)}" ${option.value === pageState.sortIndex ? 'selected' : ''}>${escapeHtml(option.label)}</option>`).join('')}
          </select>
        </label>
      </div>
      <div class="sfg-algolia-search__body">
        ${showProductsTab ? `
          <aside id="sfg-algolia-filters" class="sfg-algolia-filter-drawer" aria-label="Product filters" data-sfg-filters ${!activeProducts ? 'hidden' : ''}>
            ${renderFilters(products)}
          </aside>
          <div class="sfg-algolia-filter-drawer__overlay" aria-hidden="true" data-sfg-close-filters hidden></div>
          <section id="sfg-page-products-panel" class="sfg-algolia-search__products" role="tabpanel" aria-labelledby="sfg-page-products-tab" data-sfg-page-panel="products" ${!activeProducts ? 'hidden' : ''}>
            ${productCount ? `
              <div class="sfg-algolia-search__product-grid">
                ${products.hits.map(hit => renderProductCard(hit, algolia, products)).join('')}
              </div>
            ` : '<p class="sfg-algolia-search__no-products">No products match your filters.</p>'}
            <p class="visually-hidden" role="status" tabindex="-1" data-sfg-results-status>${productCount ? `${products.hits.length} of ${productCount} products loaded.` : 'No products match the selected filters.'}</p>
            ${pageState.productPage + 1 < products.nbPages ? '<button class="sfg-algolia-search__load-more" type="button" data-sfg-load-more>Load More</button>' : ''}
          </section>
        ` : ''}
        ${contentCount ? `
          <section id="sfg-page-content-panel" class="sfg-algolia-search__content" role="tabpanel" aria-labelledby="sfg-page-content-tab" data-sfg-page-panel="content" ${activeProducts ? 'hidden' : ''}>
            ${renderContentGroup('Education &amp; Recipes', articles.hits || [], 'articles', { query })}
            ${renderContentGroup('Support', pages.hits || [], 'pages', { query })}
          </section>
        ` : ''}
      </div>
    `;

    mount.classList.toggle('is-content-active', !activeProducts);
    document.documentElement.classList.add('sfg-algolia-search-ready');
    document.documentElement.classList.remove('sfg-algolia-search-booting');
    reloadProductWidgets(mount);
    restoreFocus(mount, focusKey);
  };

  const runPageSearch = async (mount, query, { appendProducts = false, focusKey = '' } = {}) => {
    if (appendProducts && pageState.loadingMore) return;
    window.clearTimeout(pageSearchTimer);
    pageSearchTimer = undefined;

    const requestId = ++pageState.requestId;
    const requestedPage = appendProducts ? pageState.productPage + 1 : 0;
    const snapshot = createPageSnapshot(requestedPage);
    mount.setAttribute('aria-busy', 'true');
    const filterTrigger = mount.querySelector('[data-sfg-open-filters]');
    filterTrigger?.setAttribute('aria-disabled', 'true');

    if (appendProducts) {
      pageState.loadingMore = true;
      const button = mount.querySelector('[data-sfg-load-more]');
      if (button) {
        button.disabled = true;
        button.textContent = 'Loading…';
      }
    }

    try {
      const response = await requestPageResults(query, snapshot, { loadMore: appendProducts });
      if (requestId !== pageState.requestId) return;

      const nextHits = response.products.hits || [];
      const firstNewObjectId = appendProducts ? nextHits[0]?.objectID : null;
      pageState.productHits = appendProducts ? pageState.productHits.concat(nextHits) : nextHits;
      pageState.productPage = requestedPage;
      pageState.sortIndex = response.sortIndex;
      pageState.appliedMinPrice = snapshot.minPrice;
      pageState.appliedMaxPrice = snapshot.maxPrice;
      response.products.hits = pageState.productHits;
      pageState.results = response;
      renderPageResults(
        mount,
        response,
        firstNewObjectId ? `product:${firstNewObjectId}` : focusKey,
      );
    } catch (error) {
      if (requestId === pageState.requestId) {
        mount.hidden = true;
        document.documentElement.classList.remove('sfg-algolia-search-ready');
        document.documentElement.classList.remove('sfg-algolia-search-booting');
      }
    } finally {
      if (appendProducts) pageState.loadingMore = false;
      if (requestId === pageState.requestId) {
        mount.setAttribute('aria-busy', 'false');
        const currentTrigger = document.querySelector('[data-sfg-algolia-search] [data-sfg-open-filters]');
        currentTrigger?.removeAttribute('aria-disabled');
      }
    }
  };

  const schedulePageSearch = (mount, query, options = {}) => {
    window.clearTimeout(pageSearchTimer);
    pageSearchTimer = window.setTimeout(() => runPageSearch(mount, query, options), 120);
  };

  const resetProductPage = () => {
    pageState.productPage = 0;
    pageState.productHits = [];
    pageState.loadingMore = false;
  };

  const setPageTab = (mount, selectedTab) => {
    pageState.activeTab = selectedTab;
    closeMobileFilters(mount, { restoreFocus: false });
    mount.querySelectorAll('[data-sfg-page-tab]').forEach(tab => {
      const selected = tab.dataset.sfgPageTab === selectedTab;
      tab.classList.toggle('is-active', selected);
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.tabIndex = selected ? 0 : -1;
    });
    mount.querySelectorAll('[data-sfg-page-panel]').forEach(panel => {
      panel.hidden = panel.dataset.sfgPagePanel !== selectedTab;
    });
    const activeProducts = selectedTab === 'products';
    const toolbar = mount.querySelector('.sfg-algolia-search__toolbar');
    const filters = mount.querySelector('[data-sfg-filters]');
    if (toolbar) toolbar.hidden = !activeProducts;
    if (filters) filters.hidden = !activeProducts;
    mount.classList.toggle('is-content-active', !activeProducts);
  };

  const setFilterBackgroundInert = (mount, inert) => {
    mount.querySelectorAll('.sfg-algolia-search__header, .sfg-algolia-search__toolbar, .sfg-algolia-search__products')
      .forEach(element => { element.inert = inert; });
  };

  const openMobileFilters = (mount, trigger) => {
    if (
      !window.matchMedia(MOBILE_FILTER_QUERY).matches
      || mount.getAttribute('aria-busy') === 'true'
    ) return;
    const drawer = mount.querySelector('[data-sfg-filters]');
    const overlay = mount.querySelector('.sfg-algolia-filter-drawer__overlay');
    if (!drawer) return;

    filterReturnFocus = trigger;
    mobileFilterSnapshot = captureFilterState();
    drawer.hidden = false;
    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-modal', 'true');
    drawer.setAttribute('aria-labelledby', 'sfg-algolia-filters-title');
    if (overlay) overlay.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    mount.classList.add('is-filter-open');
    document.documentElement.classList.add('sfg-filter-drawer-open');
    document.body.classList.add('sfg-filter-drawer-open');
    setFilterBackgroundInert(mount, true);
    restoreFilterIsolation = isolateModalBranch(mount);
    window.requestAnimationFrame(() => drawer.querySelector('[data-sfg-close-filters]')?.focus());
  };

  function closeMobileFilters(mount, { restoreFocus: shouldRestore = true, commit = false } = {}) {
    if (!mount?.classList.contains('is-filter-open')) return;
    const drawer = mount.querySelector('[data-sfg-filters]');
    const overlay = mount.querySelector('.sfg-algolia-filter-drawer__overlay');
    if (!commit) restoreFilterState(mount, mobileFilterSnapshot);
    mobileFilterSnapshot = undefined;
    mount.classList.remove('is-filter-open');
    document.documentElement.classList.remove('sfg-filter-drawer-open');
    document.body.classList.remove('sfg-filter-drawer-open');
    setFilterBackgroundInert(mount, false);
    mount.querySelector('[data-sfg-open-filters]')?.setAttribute('aria-expanded', 'false');
    drawer?.removeAttribute('role');
    drawer?.removeAttribute('aria-modal');
    drawer?.removeAttribute('aria-labelledby');
    if (overlay) overlay.hidden = true;
    restoreFilterIsolation?.();
    restoreFilterIsolation = undefined;
    if (shouldRestore && filterReturnFocus?.isConnected) filterReturnFocus.focus();
  }

  const clearFilters = () => {
    pageState.productType.clear();
    pageState.vanillaGrade.clear();
    pageState.minPrice = '';
    pageState.maxPrice = '';
    resetProductPage();
  };

  const initializeInputModality = () => {
    document.addEventListener('pointerdown', () => {
      document.documentElement.classList.add('sfg-pointer-input');
      document.documentElement.classList.remove('sfg-keyboard-input');
    }, true);

    document.addEventListener('keydown', event => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      document.documentElement.classList.remove('sfg-pointer-input');
      document.documentElement.classList.add('sfg-keyboard-input');
    }, true);
  };

  const initializeTabKeyboard = () => {
    document.addEventListener('keydown', event => {
      const tab = event.target.closest('[role="tab"]');
      if (
        !tab
        || !tab.closest('.predictive-search__overlay, .sfg-algolia-search')
        || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)
      ) return;
      const tabs = [...tab.closest('[role="tablist"]')?.querySelectorAll('[role="tab"]') || []]
        .filter(item => !item.hidden);
      if (!tabs.length) return;
      const current = tabs.indexOf(tab);
      let next = current;
      if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
      if (event.key === 'ArrowRight') next = (current + 1) % tabs.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = tabs.length - 1;
      event.preventDefault();
      tabs[next].focus();
      tabs[next].click();
    });
  };

  const initializeSearchPage = () => {
    const mount = document.querySelector('[data-sfg-algolia-search]');
    if (!mount) return;

    const query = new URLSearchParams(window.location.search).get('q')?.trim() || mount.dataset.query?.trim();
    if (!query) {
      mount.hidden = true;
      mount.setAttribute('aria-busy', 'false');
      return;
    }

    document.documentElement.classList.add('sfg-algolia-search-booting');
    runPageSearch(mount, query);

    document.addEventListener('click', event => {
      const currentMount = event.target.closest('[data-sfg-algolia-search]');
      if (!currentMount) return;
      const tab = event.target.closest('[data-sfg-page-tab]');
      if (tab) {
        setPageTab(currentMount, tab.dataset.sfgPageTab);
        return;
      }

      const filterTrigger = event.target.closest('[data-sfg-open-filters]');
      if (filterTrigger) {
        openMobileFilters(currentMount, filterTrigger);
        return;
      }

      if (event.target.closest('[data-sfg-apply-filters]')) {
        const focusKey = getFocusKey(filterReturnFocus) || 'filter-trigger';
        closeMobileFilters(currentMount, { commit: true });
        resetProductPage();
        runPageSearch(currentMount, query, { focusKey });
        return;
      }

      if (event.target.closest('[data-sfg-close-filters]')) {
        closeMobileFilters(currentMount);
        return;
      }

      if (event.target.closest('[data-sfg-clear-filters]')) {
        const mobile = window.matchMedia(MOBILE_FILTER_QUERY).matches;
        const focusKey = mobile ? 'filter-trigger' : 'tab:products';
        closeMobileFilters(currentMount, { commit: true });
        clearFilters();
        runPageSearch(currentMount, query, { focusKey });
        return;
      }

      const loadMore = event.target.closest('[data-sfg-load-more]');
      if (loadMore) {
        runPageSearch(currentMount, query, { appendProducts: true, focusKey: 'load-more' });
      }
    }, true);

    document.addEventListener('change', event => {
      const currentMount = event.target.closest('[data-sfg-algolia-search]');
      if (!currentMount) return;
      const checkbox = event.target.closest('[data-sfg-facet]');
      if (checkbox) {
        const selected = checkbox.dataset.sfgFacet === 'product-type'
          ? pageState.productType
          : pageState.vanillaGrade;
        if (checkbox.checked) selected.add(checkbox.value);
        else selected.delete(checkbox.value);
        if (window.matchMedia(MOBILE_FILTER_QUERY).matches) return;
        const focusKey = getFocusKey(checkbox);
        resetProductPage();
        runPageSearch(currentMount, query, { focusKey });
        return;
      }

      const sort = event.target.closest('[data-sfg-sort]');
      if (sort) {
        pageState.sortIndex = sort.value;
        resetProductPage();
        runPageSearch(currentMount, query, { focusKey: 'sort' });
      }
    });

    document.addEventListener('input', event => {
      if (!event.target.closest('[data-sfg-algolia-search]')) return;
      const price = event.target.closest('[data-sfg-price]');
      if (!price) return;
      if (price.dataset.sfgPrice === 'min') pageState.minPrice = price.value;
      else pageState.maxPrice = price.value;
    });

    document.addEventListener('focusout', event => {
      const currentMount = event.target.closest('[data-sfg-algolia-search]');
      if (!currentMount) return;
      const price = event.target.closest('[data-sfg-price]');
      if (!price || window.matchMedia(MOBILE_FILTER_QUERY).matches) return;
      if (event.relatedTarget?.matches?.('[data-sfg-price]')) return;
      if (!hasPendingPriceFilter()) return;
      resetProductPage();
      schedulePageSearch(currentMount, query, { focusKey: getFocusKey(price) });
    });

    document.addEventListener('keydown', event => {
      const currentMount = event.target.closest('[data-sfg-algolia-search]');
      if (!currentMount) return;
      const drawer = currentMount.querySelector('[data-sfg-filters]');
      if (currentMount.classList.contains('is-filter-open')) {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeMobileFilters(currentMount);
          return;
        }
        if (event.key === 'Tab' && drawer) {
          const focusable = getFocusable(drawer);
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last?.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first?.focus();
          }
        }
      }

      const price = event.target.closest('[data-sfg-price]');
      if (price && event.key === 'Enter' && !window.matchMedia(MOBILE_FILTER_QUERY).matches) {
        event.preventDefault();
        if (!hasPendingPriceFilter()) return;
        resetProductPage();
        runPageSearch(currentMount, query, { focusKey: getFocusKey(price) });
      }
    });

    window.addEventListener('resize', () => {
      const currentMount = document.querySelector('[data-sfg-algolia-search]');
      if (!window.matchMedia(MOBILE_FILTER_QUERY).matches) {
        closeMobileFilters(currentMount, { restoreFocus: false });
      }
    }, { passive: true });
  };

  const initializeDrawerInteractions = () => {
    document.addEventListener('input', event => {
      const input = event.target.closest('[data-predictive-search-terms]');
      if (input) handleDrawerInput(input);
    });

    document.addEventListener('click', event => {
      const carouselNext = event.target.closest('[data-sfg-carousel-next]');
      if (carouselNext) {
        const carousel = carouselNext.closest('.sfg-search-carousel-shell')
          ?.querySelector('[data-sfg-search-carousel]');
        const firstCard = carousel?.querySelector('.sfg-search-card, .sfg-cart-recommendation');
        if (carousel && firstCard) {
          const gap = Number.parseFloat(window.getComputedStyle(carousel).columnGap) || 0;
          const maxScroll = Math.max(0, carousel.scrollWidth - carousel.clientWidth);
          const shouldWrap = carouselNext.dataset.sfgCarouselWrap === 'true'
            || carousel.scrollLeft >= maxScroll - 1;
          carousel[shouldWrap ? 'scrollTo' : 'scrollBy']({
            left: shouldWrap ? 0 : firstCard.getBoundingClientRect().width + gap,
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          });
        }
        return;
      }

      const clearButton = event.target.closest('[data-sfg-search-clear]');
      if (clearButton) {
        const form = clearButton.closest('[data-search-form]');
        const input = form?.querySelector('[data-predictive-search-terms]');
        if (input) {
          input.value = '';
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.focus();
        }
        return;
      }

      const tab = event.target.closest('[data-sfg-search-tab]');
      if (!tab) return;
      const scope = tab.closest('.predictive-search__results, .sfg-search-default');
      const selectedPanel = tab.dataset.sfgSearchTab;
      scope?.querySelectorAll('[data-sfg-search-tab]').forEach(button => {
        const selected = button === tab;
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-selected', selected ? 'true' : 'false');
        button.tabIndex = selected ? 0 : -1;
      });
      scope?.querySelectorAll('[data-sfg-search-panel]').forEach(panel => {
        panel.hidden = panel.dataset.sfgSearchPanel !== selectedPanel;
      });
      refreshCarousels(scope);
    });
  };

  const initialize = () => {
    initializeInputModality();
    initializeTabKeyboard();
    initializeDrawerInteractions();
    initializeSearchDrawer();
    initializeSearchPage();
    initializeCarousels(document);
    window.addEventListener('resize', () => {
      document.querySelectorAll('[data-sfg-search-carousel]').forEach(updateCarouselProgress);
    }, { passive: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
